import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { QRCodeSVG } from 'qrcode.react'
import {
  Radio,
  X,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Scan,
  Sliders,
  RotateCw
} from 'lucide-react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
  isOpen: boolean
  onClose: () => void
  onCompletarModulacion: (
    expId: string,
    resultado: 'VERDE' | 'ROJO',
    detalleIncidencia?: {
      codigo: string
      descripcion: string
      solucion: string
    }
  ) => void
  onAbrirDocumentoCompleto?: (tipo: 'DODA' | 'PEDIMENTO') => void
}

type ModoSeleccion = 'ALEATORIO' | 'FORZAR_VERDE' | 'FORZAR_ROJO'

interface RetoRojoOpcion {
  id: string
  titulo: string
  codigo: string
  descripcion: string
  solucion: string
}

const OPCIONES_RECONOCIMIENTO_ROJO: RetoRojoOpcion[] = [
  {
    id: 'sello_divergente',
    titulo: 'Discrepancia en Precinto Fiscal / Sello de Seguridad',
    codigo: 'INC-ANAM-043-SELLO',
    descripcion:
      'El verificador detecta que el sello físico colocado difiere del declarado en el Pedimento / DODA o presenta signos de manipulación.',
    solucion:
      'Presentar escrito de rectificación de precinto con testimonio de la naviera y solicitar supervisión de apertura con Agente Aduanal.'
  },
  {
    id: 'etiquetado_nom',
    titulo: 'Incumplimiento de Etiquetado Comercial (NOM-050 / NOM-051)',
    codigo: 'INC-ANAM-043-NOM',
    descripcion:
      'Las cajas no exhiben la información comercial obligatoria en español (país de origen, importador, advertencias de seguridad).',
    solucion:
      'Solicitar traslado a Unidad de Verificación Acreditada (UVA) en depósito fiscal o etiquetado en andén de inspección previo a liberación.'
  },
  {
    id: 'clasificacion_arancelaria',
    titulo: 'Duda Razonable en Fracción Arancelaria (TIGIE)',
    codigo: 'INC-ANAM-043-TIGIE',
    descripcion:
      'El verificador aduanal sospecha subvaluación arancelaria o mala clasificación de la partida 8504 a 8517 para evadir cuota compensatoria.',
    solucion:
      'Toma de muestra de mercancía por laboratorio central de ANAM y otorgamiento de fianza para garantizar el interés fiscal.'
  },
  {
    id: 'balanza_sicrefis',
    titulo: 'Alerta SICREFIS [122] - Sin Saldo en Balanza de Recinto',
    codigo: 'SICREFIS-122',
    descripcion:
      'El sistema automatizado no registra el descargo electrónico de la terminal SSA Manzanillo en la balanza fiscalizada.',
    solucion:
      'Gestionar la vinculación inmediata de la orden BP1 y tarifa de almacenaje en el sistema de ASIPONA/SSA para liberar el candado electrónico.'
  }
]

// Reproductor de tonos sintéticos con Web Audio API para feedback háptico/auditivo
const reproducirTono = (tipo: 'beep' | 'success' | 'alert' | 'scan') => {
  try {
    const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioCtxClass) return
    const ctx = new AudioCtxClass()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)

    if (tipo === 'scan') {
      osc.type = 'sine'
      osc.frequency.setValueAtTime(600, ctx.currentTime)
      osc.frequency.linearRampToValueAtTime(1200, ctx.currentTime + 0.12)
      gain.gain.setValueAtTime(0.08, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.15)
    } else if (tipo === 'beep') {
      osc.type = 'square'
      osc.frequency.setValueAtTime(800, ctx.currentTime)
      gain.gain.setValueAtTime(0.06, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.1)
    } else if (tipo === 'success') {
      osc.type = 'triangle'
      osc.frequency.setValueAtTime(523.25, ctx.currentTime) // Do
      osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.12) // Mi
      osc.frequency.setValueAtTime(783.99, ctx.currentTime + 0.24) // Sol
      gain.gain.setValueAtTime(0.12, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.45)
    } else if (tipo === 'alert') {
      osc.type = 'sawtooth'
      osc.frequency.setValueAtTime(280, ctx.currentTime)
      osc.frequency.setValueAtTime(200, ctx.currentTime + 0.15)
      gain.gain.setValueAtTime(0.15, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.35)
    }
  } catch (e) {
    // Si el navegador bloquea audio sin interacción previa, no afecta la UI
  }
}

export const ModalModulacionDODA: React.FC<Props> = ({
  expediente,
  isOpen,
  onClose,
  onCompletarModulacion,
  onAbrirDocumentoCompleto
}) => {
  const [fase, setFase] = useState<'ESPERANDO' | 'ESCANEANDO' | 'RESULTADO'>('ESPERANDO')
  const [modo, setModo] = useState<ModoSeleccion>('ALEATORIO')
  const [retoRojoSeleccionado, setRetoRojoSeleccionado] = useState<string>(OPCIONES_RECONOCIMIENTO_ROJO[0].id)
  const [resultado, setResultado] = useState<'VERDE' | 'ROJO' | null>(null)
  const [folioSelloDigital, setFolioSelloDigital] = useState<string>('')

  // Resetear estados al abrir el modal con un nuevo expediente
  useEffect(() => {
    if (isOpen) {
      setFase('ESPERANDO')
      setResultado(null)
      setFolioSelloDigital('')
    }
  }, [isOpen, expediente.id])

  if (!isOpen) return null

  // Folio DODA auténtico generado a partir del expediente
  const folioDODA = `DODA-2026-${expediente.actores.agenteAduanal.aduana.includes('160') ? '160' : 'MX'}-${expediente.folio.replace(/[^0-9]/g, '').slice(-6) || '884920'}`
  const numeroPedimentoFormateado = `26  16  ${expediente.actores.agenteAduanal.patente || '3450'}  ${expediente.folio.replace(/[^0-9]/g, '').padStart(7, '8004219').slice(-7)}`
  const qrString = `SAT-DODA|ADU:160|PAT:${expediente.actores.agenteAduanal.patente}|PED:${numeroPedimentoFormateado.replace(/\s+/g, '')}|CONT:${expediente.contenedor.numero}|VEH:${expediente.actores.transportista.placas}|CAAT:${expediente.actores.transportista.caat}|SEC:8829104`

  // Iniciar el proceso de modulación aduanera
  const ejecutarModulacion = () => {
    setFase('ESCANEANDO')
    reproducirTono('scan')

    // Efecto de escaneo óptico durante 2.2 segundos
    let contadorBeeps = 0
    const intervalBeeps = setInterval(() => {
      contadorBeeps++
      reproducirTono('beep')
      if (contadorBeeps >= 3) clearInterval(intervalBeeps)
    }, 450)

    setTimeout(() => {
      let resultadoFinal: 'VERDE' | 'ROJO' = 'VERDE'

      if (modo === 'FORZAR_VERDE') {
        resultadoFinal = 'VERDE'
      } else if (modo === 'FORZAR_ROJO') {
        resultadoFinal = 'ROJO'
      } else {
        // En aduanas reales mexicanas, aprox 85% Verde y 15% Rojo
        resultadoFinal = Math.random() > 0.18 ? 'VERDE' : 'ROJO'
      }

      setResultado(resultadoFinal)
      setFase('RESULTADO')

      if (resultadoFinal === 'VERDE') {
        reproducirTono('success')
        const randomHash = Math.random().toString(36).substring(2, 8).toUpperCase()
        setFolioSelloDigital(`ANAM-ZLO-OK-2026-${randomHash}`)
      } else {
        reproducirTono('alert')
      }
    }, 2200)
  }

  // Confirmar y aplicar cambio al expediente
  const confirmarYSalir = () => {
    if (!resultado) return

    if (resultado === 'VERDE') {
      onCompletarModulacion(expediente.id, 'VERDE')
    } else {
      const reto = OPCIONES_RECONOCIMIENTO_ROJO.find((r) => r.id === retoRojoSeleccionado) || OPCIONES_RECONOCIMIENTO_ROJO[0]
      onCompletarModulacion(expediente.id, 'ROJO', {
        codigo: reto.codigo,
        descripcion: reto.descripcion,
        solucion: reto.solucion
      })
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/85 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 20 }}
        className="relative w-full max-w-4xl bg-slate-900 border border-slate-700/70 rounded-3xl shadow-2xl shadow-cyan-950/40 overflow-hidden text-slate-100 my-auto"
      >
        {/* ENCABEZADO OFICIAL ANAM / SAT */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-950/40 border-b border-white/10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-black shadow-inner">
              <Radio className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  ANAM · SAT
                </span>
                <span className="text-xs text-slate-400 font-mono">
                  Aduana de Manzanillo #160 · Garita Fiscal Norte
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-black text-white tracking-tight flex items-center gap-2">
                Mecanismo de Selección Automatizada (Modulación DODA)
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* COLUMNA IZQUIERDA: TARJETA DODA Y CÓDIGO QR BIDIMENSIONAL */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <div className="bg-slate-950/90 rounded-2xl p-5 border border-slate-800 relative overflow-hidden shadow-inner">
              {/* Encabezado del DODA */}
              <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-3">
                <div>
                  <p className="text-[10px] font-black tracking-wider uppercase text-slate-400">
                    Documento de Operación para Despacho Aduanero
                  </p>
                  <p className="font-mono text-xs font-bold text-amber-400">{folioDODA}</p>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  Art. 35-43 LA
                </span>
              </div>

              {/* Contenedor del QR con Escáner Láser Visual */}
              <div className="relative mx-auto my-2 p-4 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center w-52 h-52">
                <QRCodeSVG
                  value={qrString}
                  size={170}
                  level="H"
                  includeMargin={false}
                  className="rounded-lg"
                />

                {/* Línea animada de escáner láser */}
                {fase === 'ESCANEANDO' && (
                  <motion.div
                    initial={{ top: 16 }}
                    animate={{ top: [16, 180, 16] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute left-4 right-4 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_12px_#22d3ee] z-20 pointer-events-none"
                  />
                )}
              </div>

              {/* Datos Resumen del Embarque */}
              <div className="space-y-2 mt-4 text-xs font-mono">
                <div className="flex justify-between py-1 border-b border-slate-800/80">
                  <span className="text-slate-400 font-sans">Pedimento:</span>
                  <span className="font-bold text-slate-200">{numeroPedimentoFormateado}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80">
                  <span className="text-slate-400 font-sans">Contenedor:</span>
                  <span className="font-bold text-cyan-400">{expediente.contenedor.numero}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80">
                  <span className="text-slate-400 font-sans">Tracto / Placas:</span>
                  <span className="font-bold text-slate-200">{expediente.actores.transportista.placas}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-slate-800/80">
                  <span className="text-slate-400 font-sans">CAAT Transportista:</span>
                  <span className="font-bold text-amber-400">{expediente.actores.transportista.caat}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-slate-400 font-sans">Patente Aduanal:</span>
                  <span className="font-bold text-slate-200">{expediente.actores.agenteAduanal.patente} (Aduana 160)</span>
                </div>
              </div>

              {/* Botón para ver Hoja DODA completa */}
              {onAbrirDocumentoCompleto && (
                <button
                  type="button"
                  onClick={() => onAbrirDocumentoCompleto('DODA')}
                  className="w-full mt-3 py-2 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-slate-700"
                >
                  <FileText className="w-3.5 h-3.5 text-cyan-400" />
                  <span>Ver Formato DODA Completo</span>
                </button>
              )}
            </div>
          </div>

          {/* COLUMNA DERECHA: SEMÁFORO FISCAL, MODOS PEDAGÓGICOS Y RESULTADO */}
          <div className="lg:col-span-7 flex flex-col justify-between gap-5">
            {/* PANEL DE CONTROL DE MODULACIÓN */}
            <div className="bg-slate-950/50 rounded-2xl p-5 border border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <Sliders className="w-4 h-4 text-emerald-400" />
                  Modo de Modulación Aduanera
                </span>
                <span className="text-[11px] text-slate-500">Práctica Institucional</span>
              </div>

              {/* Selector de Modos (Aleatorio vs Forzar) */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setModo('ALEATORIO')}
                  disabled={fase === 'ESCANEANDO'}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    modo === 'ALEATORIO'
                      ? 'bg-cyan-500/15 border-cyan-500/50 text-white shadow-sm'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                  }`}
                >
                  <p className="text-xs font-black">Aleatorio Real</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">85% Verde / 15% Rojo</p>
                </button>

                <button
                  type="button"
                  onClick={() => setModo('FORZAR_VERDE')}
                  disabled={fase === 'ESCANEANDO'}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    modo === 'FORZAR_VERDE'
                      ? 'bg-emerald-500/15 border-emerald-500/50 text-white shadow-sm'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                  }`}
                >
                  <p className="text-xs font-black text-emerald-400">Forzar Verde</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Desaduanamiento Libre</p>
                </button>

                <button
                  type="button"
                  onClick={() => setModo('FORZAR_ROJO')}
                  disabled={fase === 'ESCANEANDO'}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    modo === 'FORZAR_ROJO'
                      ? 'bg-rose-500/15 border-rose-500/50 text-white shadow-sm'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:bg-slate-850'
                  }`}
                >
                  <p className="text-xs font-black text-rose-400">Forzar Rojo</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Reconocimiento Aduanero</p>
                </button>
              </div>

              {/* Sub-selector de Incidencia si se fuerza Rojo */}
              {modo === 'FORZAR_ROJO' && (
                <div className="mt-4 pt-3 border-t border-slate-800/80">
                  <label className="block text-[11px] font-bold text-slate-400 mb-2">
                    Seleccionar Causa de Reconocimiento / Reto para el Alumno:
                  </label>
                  <select
                    value={retoRojoSeleccionado}
                    onChange={(e) => setRetoRojoSeleccionado(e.target.value)}
                    className="w-full text-xs bg-slate-900 border border-slate-700 rounded-xl px-3 py-2.5 text-slate-200 focus:outline-none focus:border-rose-500"
                  >
                    {OPCIONES_RECONOCIMIENTO_ROJO.map((opcion) => (
                      <option key={opcion.id} value={opcion.id}>
                        {opcion.codigo} · {opcion.titulo}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* VISUALIZADOR DEL SEMÁFORO FISCAL DE 3 LUCES */}
            <div className="bg-slate-950 rounded-2xl p-5 border border-slate-800 flex items-center justify-between gap-6">
              <div className="flex-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                  Estatus de Garita Automatizada
                </span>
                <h4 className="text-base font-bold text-white">
                  {fase === 'ESPERANDO' && 'Unidad alineada en sensor óptico. Listo para modular.'}
                  {fase === 'ESCANEANDO' && 'Leyendo código bidimensional DODA y consultando matriz de riesgo ANAM...'}
                  {fase === 'RESULTADO' && resultado === 'VERDE' && 'DESADUANAMIENTO LIBRE (Art. 43 Ley Aduanera)'}
                  {fase === 'RESULTADO' && resultado === 'ROJO' && 'RECONOCIMIENTO ADUANERO (Art. 43 y 44 Ley Aduanera)'}
                </h4>
                <p className="text-xs text-slate-400 mt-1">
                  {fase === 'ESPERANDO' && 'Presione el botón para activar el mecanismo de selección.'}
                  {fase === 'ESCANEANDO' && 'Balanza de recinto fiscalizado verificando descargo electrónico.'}
                  {fase === 'RESULTADO' && resultado === 'VERDE' && 'La mercancía no requiere inspección física en muelle. Autorizado para salida.'}
                  {fase === 'RESULTADO' && resultado === 'ROJO' && 'Presentarse de inmediato en Andén #4 con Verificador Oficial ANAM.'}
                </p>
              </div>

              {/* El Semáforo en Sí (Chasis de Semáforo Real) */}
              <div className="p-3 bg-black/80 rounded-2xl border-2 border-slate-700/80 flex flex-col gap-2.5 shadow-2xl shrink-0">
                {/* Luz Roja */}
                <div
                  className={`w-9 h-9 rounded-full transition-all duration-300 flex items-center justify-center ${
                    resultado === 'ROJO'
                      ? 'bg-rose-500 shadow-[0_0_24px_#f43f5e] border-2 border-white'
                      : 'bg-rose-950/40 border border-rose-900/40'
                  }`}
                >
                  {resultado === 'ROJO' && <div className="w-2.5 h-2.5 rounded-full bg-white animate-ping" />}
                </div>

                {/* Luz Ámbar / Espera */}
                <div
                  className={`w-9 h-9 rounded-full transition-all duration-300 flex items-center justify-center ${
                    fase === 'ESCANEANDO'
                      ? 'bg-amber-400 shadow-[0_0_24px_#fbbf24] border-2 border-white animate-pulse'
                      : 'bg-amber-950/40 border border-amber-900/40'
                  }`}
                />

                {/* Luz Verde */}
                <div
                  className={`w-9 h-9 rounded-full transition-all duration-300 flex items-center justify-center ${
                    resultado === 'VERDE'
                      ? 'bg-emerald-400 shadow-[0_0_24px_#34d399] border-2 border-white'
                      : 'bg-emerald-950/40 border border-emerald-900/40'
                  }`}
                >
                  {resultado === 'VERDE' && <div className="w-2.5 h-2.5 rounded-full bg-white animate-ping" />}
                </div>
              </div>
            </div>

            {/* DETALLE DEL RESULTADO O BOTÓN DE ACCIÓN */}
            <div>
              {fase === 'ESPERANDO' && (
                <button
                  type="button"
                  onClick={ejecutarModulacion}
                  className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-sm uppercase tracking-wider shadow-lg shadow-emerald-500/25 transition-all flex items-center justify-center gap-2 transform active:scale-[0.99]"
                >
                  <Scan className="w-5 h-5" />
                  <span>Escanear DODA y Modular Mercancía</span>
                </button>
              )}

              {fase === 'ESCANEANDO' && (
                <div className="w-full py-4 px-6 rounded-2xl bg-slate-800 text-slate-400 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-3 border border-slate-700 animate-pulse">
                  <RotateCw className="w-4 h-4 animate-spin text-cyan-400" />
                  <span>Consultando Servidor Central ANAM (SAAI-M3)...</span>
                </div>
              )}

              {fase === 'RESULTADO' && (
                <div className="space-y-4">
                  {resultado === 'VERDE' ? (
                    <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div className="text-xs">
                        <p className="font-bold text-emerald-300">
                          Sello Digital de Desaduanamiento Exitoso
                        </p>
                        <p className="font-mono text-[11px] text-emerald-400/90 mt-0.5">
                          {folioSelloDigital}
                        </p>
                        <p className="text-slate-300 text-[11px] mt-1">
                          El tractocamión ha recibido la señal verde en garita fiscal. La mercancía se encuentra legalmente desaduanada para internarse al país o ingresar al buque.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
                      <div className="text-xs">
                        <p className="font-bold text-rose-300">
                          Alerta de Reconocimiento Aduanero Activada
                        </p>
                        <p className="text-slate-300 text-[11px] mt-1">
                          {OPCIONES_RECONOCIMIENTO_ROJO.find((r) => r.id === retoRojoSeleccionado)?.descripcion ||
                            'Se requiere apertura física de contenedor y verificación ocular de bultos.'}
                        </p>
                        <p className="text-amber-400 text-[11px] font-bold mt-1">
                          Solución requerida:{' '}
                          {OPCIONES_RECONOCIMIENTO_ROJO.find((r) => r.id === retoRojoSeleccionado)?.solucion}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFase('ESPERANDO')}
                      className="px-4 py-3 rounded-xl border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-colors"
                    >
                      Repetir Modulación
                    </button>

                    <button
                      type="button"
                      onClick={confirmarYSalir}
                      className={`flex-1 py-3 px-5 rounded-xl font-black text-xs uppercase tracking-wider shadow-lg transition-all flex items-center justify-center gap-2 ${
                        resultado === 'VERDE'
                          ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-emerald-500/20'
                          : 'bg-rose-500 hover:bg-rose-400 text-white shadow-rose-500/20'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>
                        {resultado === 'VERDE'
                          ? 'Confirmar Desaduanamiento y Continuar'
                          : 'Registrar Incidencia de Reconocimiento'}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default ModalModulacionDODA
